import { useCallback, useEffect, useState } from 'react';
import {
  ApplicationControllerApi,
  ApplicationResponse,
} from '../openapi/yenta';
import ErrorService from '../services/ErrorService';
import { showErrorToast } from '../slices/ToastNotificationSlice';
import { useAppDispatch } from '../slices/store';
import Logger from '../utils/Logger';
import { getYentaConfiguration } from '../utils/OpenapiConfigurationUtils';

interface UseApplicationOptions {
  enabled?: boolean;
}

/**
 * Custom hook to fetch application data by ID
 * @param applicationId - The ID of the application to fetch
 * @param options - Configuration options
 * @param options.enabled - Whether to automatically fetch on mount (default: true)
 * @returns Object containing application data, loading state, error, and refetch function
 */
export const useApplication = (
  applicationId: string | undefined | null,
  options: UseApplicationOptions = {},
) => {
  const { enabled = true } = options;
  const dispatch = useAppDispatch();

  const [data, setData] = useState<ApplicationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchApplication = useCallback(
    async (id: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: applicationData } = await new ApplicationControllerApi(
          await getYentaConfiguration(),
        ).getApplication(id);

        setData(applicationData);
        return applicationData;
      } catch (e: any) {
        const errorMessage = 'Failed to fetch application data';
        Logger.error(errorMessage, e, { data: { applicationId: id } });
        ErrorService.notify(errorMessage, e, { data: { applicationId: id } });
        dispatch(
          showErrorToast(
            'Unable to load application data. Please try again after sometime.',
          ),
        );
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (applicationId && enabled) {
      fetchApplication(applicationId);
    }
  }, [applicationId, enabled, fetchApplication]);

  const refetch = useCallback(async () => {
    if (applicationId) {
      return await fetchApplication(applicationId);
    }
    return null;
  }, [applicationId, fetchApplication]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
